import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ShieldCheck, ShieldAlert, AlertTriangle, Plus } from "lucide-react";
import { toast } from "sonner";

const OUTCOMES = {
    clear:              { label: "Clear",              cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    potential_conflict: { label: "Potential conflict", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    conflict_managed:   { label: "Conflict managed",   cls: "bg-blue-50 text-blue-700 border-blue-200" },
    declined:           { label: "Declined",           cls: "bg-red-50 text-red-700 border-red-200" },
};

function fmtDate(iso) {
    return iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "";
}

function OutcomeBadge({ outcome }) {
    const o = OUTCOMES[outcome] || OUTCOMES.clear;
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${o.cls}`}>{o.label}</span>;
}

function splitParties(text) {
    return text.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
}

function RunCheckDialog({ deal, open, onClose, onDone }) {
    const [parties, setParties] = useState(deal.company_name || "");
    const [outcome, setOutcome] = useState("clear");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        const list = splitParties(parties);
        if (!list.length) { toast.error("Add at least one party to check"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/compliance/conflict-check", {
                deal_id: deal.id, parties_checked: list, outcome, notes: notes || null,
            });
            const n = (data.matches_found || []).length;
            if (n > 0) toast.warning(`${n} possible overlap${n > 1 ? "s" : ""} found — review and confirm the outcome`);
            else toast.success("Conflict check recorded — no overlaps found");
            onDone(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to run conflict check");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="conflict-dialog">
                <DialogHeader><DialogTitle className="font-display">Run conflict check</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Parties to check</Label>
                        <Textarea
                            data-testid="conflict-parties"
                            value={parties}
                            onChange={(e) => setParties(e.target.value)}
                            placeholder="One per line or comma-separated: the target, vendor, directors, known counterparties…"
                            className="text-sm min-h-[88px]"
                        />
                        <p className="text-xs text-muted-foreground">
                            Searched against your firm&apos;s existing contacts and other deals. Matches are shown after you run the check.
                        </p>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Outcome</Label>
                        <Select value={outcome} onValueChange={setOutcome}>
                            <SelectTrigger data-testid="conflict-outcome" className="h-8 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {Object.entries(OUTCOMES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">You decide the outcome — matches never set it automatically. You can change it after reviewing the results.</p>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
                        <Textarea data-testid="conflict-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="text-sm min-h-[60px]" />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button variant="outline" onClick={onClose}>Cancel</Button>
                        <Button onClick={submit} disabled={saving} data-testid="conflict-submit">{saving ? "Checking…" : "Run check"}</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function CheckRow({ check, onUpdated }) {
    const [outcome, setOutcome] = useState(check.outcome);
    const [saving, setSaving] = useState(false);
    const matches = check.matches_found || [];
    const dirty = outcome !== check.outcome;

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/compliance/conflict-check/${check.id}`, { outcome });
            toast.success("Outcome updated");
            onUpdated(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update outcome");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rounded-md border border-border p-3 space-y-2" data-testid={`conflict-check-${check.id}`}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <OutcomeBadge outcome={check.outcome} />
                    <span className="text-xs text-muted-foreground">{fmtDate(check.created_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={outcome} onValueChange={setOutcome}>
                        <SelectTrigger className="h-7 text-xs w-44" data-testid={`conflict-outcome-edit-${check.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {Object.entries(OUTCOMES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    {dirty && <Button size="sm" className="h-7" onClick={save} disabled={saving} data-testid={`conflict-outcome-save-${check.id}`}>Save</Button>}
                </div>
            </div>
            <div className="text-xs text-muted-foreground">Checked: {(check.parties_checked || []).join(", ")}</div>
            {matches.length > 0 ? (
                <div className="rounded bg-amber-50 border border-amber-200 p-2 space-y-1" data-testid="conflict-matches">
                    <div className="flex items-center gap-1 text-xs font-medium text-amber-800">
                        <AlertTriangle className="w-3 h-3" /> {matches.length} possible overlap{matches.length > 1 ? "s" : ""} — review and confirm the outcome
                    </div>
                    {matches.map((m, i) => (
                        <div key={`${m.matched_id}-${i}`} className="text-xs text-amber-900">
                            {m.matched_type === "deal"
                                ? <a className="underline" href={`/deals/${m.matched_id}`}>{m.matched_name}</a>
                                : <span className="font-medium">{m.matched_name}</span>}
                            {" — "}{m.note}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-xs text-emerald-700">No overlaps found in existing contacts or deals.</div>
            )}
            {check.notes && <div className="text-xs">{check.notes}</div>}
        </div>
    );
}

export default function ConflictCheckPanel({ deal, onDealChanged }) {
    const [checks, setChecks] = useState(null);
    const [open, setOpen] = useState(false);
    const [params, setParams] = useSearchParams();

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/compliance/conflict-check/deal/${deal.id}`);
            setChecks(data);
        } catch {
            setChecks([]);
        }
    }, [deal.id]);

    useEffect(() => { load(); }, [load]);

    // Deep link from "new deal" prompt: /deals/:id?conflict=1
    useEffect(() => {
        if (params.get("conflict") === "1") {
            setOpen(true);
            const next = new URLSearchParams(params);
            next.delete("conflict");
            setParams(next, { replace: true });
        }
    }, [params, setParams]);

    const latest = checks && checks[0];
    const done = (rec) => {
        setOpen(false);
        setChecks((c) => [rec, ...(c || [])]);
        onDealChanged && onDealChanged();
    };
    const updated = (rec) => {
        setChecks((c) => (c || []).map((x) => (x.id === rec.id ? rec : x)));
        onDealChanged && onDealChanged();
    };

    return (
        <div className="space-y-2" data-testid="conflict-panel">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                    {latest && (latest.outcome === "clear" || latest.outcome === "conflict_managed")
                        ? <ShieldCheck className="w-4 h-4 text-emerald-600" />
                        : <ShieldAlert className={`w-4 h-4 ${latest ? "text-amber-600" : "text-muted-foreground"}`} />}
                    {checks === null ? <span className="text-muted-foreground">Loading…</span>
                        : latest ? <OutcomeBadge outcome={latest.outcome} />
                        : <span className="text-muted-foreground" data-testid="conflict-none">No conflict check recorded</span>}
                </div>
                <Button size="sm" variant="outline" className="h-7" onClick={() => setOpen(true)} data-testid="conflict-run-btn">
                    <Plus className="w-3 h-3 mr-1" /> {latest ? "Re-run check" : "Run check"}
                </Button>
            </div>
            {(checks || []).map((c) => <CheckRow key={c.id} check={c} onUpdated={updated} />)}
            {open && <RunCheckDialog deal={deal} open={open} onClose={() => setOpen(false)} onDone={done} />}
        </div>
    );
}
